
import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { ArrowLeft, DollarSign, Bitcoin, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type CryptoCurrencyType = 'USDT' | 'BNB' | 'SOL' | 'USDC';

const Transaction: React.FC = () => {
  const { setCurrentScreen, createTransaction } = useApp();
  const { toast } = useToast();
  
  const [amount, setAmount] = useState('');
  const [cryptoCurrency, setCryptoCurrency] = useState<CryptoCurrencyType>('USDT');
  const [cryptoAmount, setCryptoAmount] = useState('');
  const [errors, setErrors] = useState({ amount: '' });
  const [isValid, setIsValid] = useState(false);

  // Conversion rates (simplified for demo)
  const conversionRates: Record<CryptoCurrencyType, number> = {
    'USDT': 1,
    'BNB': 0.004,
    'SOL': 0.1,
    'USDC': 1,
  };

  const calculateCryptoAmount = (fiatAmount: string) => {
    const num = parseFloat(fiatAmount || '0');
    if (isNaN(num)) return '';
    
    const converted = (num * conversionRates[cryptoCurrency]).toFixed(6);
    setCryptoAmount(converted);
    return converted;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value);
    calculateCryptoAmount(value);
    
    // Run validation when the amount changes
    validateAmount(value);
  };

  const handleCurrencyChange = (currency: CryptoCurrencyType) => {
    setCryptoCurrency(currency);
    calculateCryptoAmount(amount);
  };

  // Separate function to validate just the amount
  const validateAmount = (value: string) => {
    let error = '';
    
    // Only validate amount if it's not empty
    if (value) {
      const num = parseFloat(value);
      
      if (isNaN(num)) {
        error = 'Please enter a valid amount';
      } else if (num < 10) {
        error = 'Minimum amount is $10';
      } else if (num > 1000) {
        error = 'Maximum amount is $1000';
      }
    }
    
    setErrors(prev => ({ ...prev, amount: error }));
    return error === '';
  };

  // Effect to update isValid state whenever errors change
  useEffect(() => {
    // Form is valid if there are no errors and amount is not empty
    const amountValid = amount !== '' && errors.amount === '';
    setIsValid(amountValid);
  }, [amount, errors]);

  const handleSubmit = () => {
    // Additional validation before proceeding
    if (isValid) {
      createTransaction(amount, cryptoAmount, cryptoCurrency);
      setCurrentScreen('transaction-confirmation');
      toast({
        title: "Transaction initiated",
        description: `Please send ${cryptoAmount} ${cryptoCurrency} to continue`,
      });
    }
  };

  const currencyOptions: CryptoCurrencyType[] = ['USDT', 'BNB', 'SOL', 'USDC'];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center mb-6 pt-4">
        <button 
          onClick={() => setCurrentScreen('dashboard')}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold ml-4">Convert Crypto to Cash</h1>
      </div>

      <div className="flex-1 px-4">
        <div className="mobile-card mb-6">
          <div className="mb-4">
            <label className="input-label" htmlFor="amount">Amount (USD)</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <DollarSign className="w-5 h-5 text-gray-400" />
              </div>
              <input
                id="amount"
                type="number"
                className="input-field pl-10"
                placeholder="0.00"
                value={amount}
                onChange={handleAmountChange}
              />
            </div>
            {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
          </div>

          <div className="mb-4">
            <label className="input-label">Select Cryptocurrency</label>
            <div className="grid grid-cols-2 gap-2">
              {currencyOptions.map((currency) => (
                <button
                  key={currency}
                  type="button"
                  onClick={() => handleCurrencyChange(currency)}
                  className={`py-3 px-4 border rounded-lg ${
                    cryptoCurrency === currency
                      ? 'border-neobank-primary bg-neobank-primary/10 text-neobank-primary'
                      : 'border-gray-200 text-gray-700'
                  } flex items-center justify-center`}
                >
                  <Bitcoin className="w-4 h-4 mr-2" />
                  {currency}
                </button>
              ))}
            </div>
          </div>

          {amount && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Conversion Rate</span>
                <span className="font-medium">1 USD = {conversionRates[cryptoCurrency]} {cryptoCurrency}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Fee</span>
                <span className="font-medium">$2.00</span>
              </div>
              <div className="border-t border-gray-200 my-2 pt-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">You'll Send</span>
                  <span className="font-bold">{cryptoAmount} {cryptoCurrency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">You'll Receive</span>
                  <span className="font-bold">${parseFloat(amount || '0') - 2} USD</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mb-4">
          <h3 className="font-medium mb-2">How it works</h3>
          <ol className="pl-4 space-y-2 text-sm text-gray-600">
            <li>Enter the amount you want to receive in cash</li>
            <li>Send the crypto amount to the provided address</li>
            <li>Once confirmed, you'll receive a QR code</li>
            <li>Visit an ATM and scan the QR code to withdraw cash</li>
          </ol>
        </div>

        <button 
          onClick={handleSubmit} 
          className="btn-primary flex items-center justify-center"
          disabled={!isValid}
        >
          Continue <ArrowRight className="ml-2 w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Transaction;
